import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { LuX } from 'react-icons/lu';
import { MdApartment, MdWork } from 'react-icons/md';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import Loading from '@/components/Loading';
import Select from '@/components/Select';
import { Tag } from '@/components/Tag';
import useFetchIam from '@/features/agent/api/useFetchIam';
import useActiveUsers from '@/features/agent/active_users/hooks/useActiveUsers';
import UsersMenu from '@/features/agent/components/UsersMenu';
import useFetchCandidateProfiles, {
  type Request,
} from '@/features/agent/candidates/api/useFetchCandidateProfiles';
import useFetchCandidateBoards from '@/features/agent/candidates/api/useFetchCandidateBoards';
import useUpdateCandidateProfile from '@/features/agent/candidates/api/useUpdateCandidateProfile';
import { showErrorToast } from '@/features/agent/job_change_processes/lib/toast';

const ALL_STATUS_VALUE = 'all';
const ALL_STATUS_LABEL = '全てのステータス';

type Props = {
  selectedId: number | null;
  onSelect: (id: number, name: string) => void;
};

export default function CandidateSelectStep({ selectedId, onSelect }: Props) {
  const [inputValue, setInputValue] = useState('');
  const [keyword, setKeyword] = useState('');
  const [boardId, setBoardId] = useState('');

  // 入力値を300msデバウンスしてから検索キーワードに反映
  useEffect(() => {
    const timer = setTimeout(() => {
      setKeyword(inputValue.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data: boardsData } = useFetchCandidateBoards();
  const { data: iam } = useFetchIam();
  const { data: activeUsersData } = useActiveUsers();
  const queryClient = useQueryClient();
  const { mutateAsync: updateCandidate } = useUpdateCandidateProfile();

  const handleUpdateAssignees = async (candidateProfileId: number, userIds: number[]) => {
    try {
      await updateCandidate({ candidateProfileId, organization_user_ids: userIds });
      await queryClient.invalidateQueries({ queryKey: ['candidateProfiles'] });
    } catch {
      showErrorToast('担当者の更新に失敗しました');
    }
  };

  // フリーワード・ステータス（ボード）を1つの検索条件にまとめる
  const query = useMemo<Request | undefined>(() => {
    const q: Request = {};
    if (keyword) q.keyword = keyword;
    if (boardId) q.candidate_board_ids = [Number(boardId)];
    return Object.keys(q).length > 0 ? q : undefined;
  }, [keyword, boardId]);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useFetchCandidateProfiles(query);

  const candidates = data?.pages.flatMap((page) => page.records) ?? [];
  const hasSearchConditions = inputValue !== '' || boardId !== '';

  // リスト末尾付近までスクロールしたら次ページを読み込む
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (isFetchingNextPage || !hasNextPage) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 300) {
      void fetchNextPage();
    }
  };

  const clearSearch = () => {
    setInputValue('');
    setKeyword('');
    setBoardId('');
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* 検索フィルタ */}
      <div className="flex shrink-0 items-center gap-lg px-lg py-md">
        <div className="flex items-center gap-md">
          <Input
            type="text"
            placeholder="キーワードで検索"
            search
            className="w-84"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <div className="w-59 shrink-0">
            <Select
              className="w-full"
              items={[
                { value: ALL_STATUS_VALUE, label: ALL_STATUS_LABEL },
                ...(boardsData?.records ?? []).map((board) => ({
                  value: board.id.toString(),
                  label: board.title,
                })),
              ]}
              value={boardId || ALL_STATUS_VALUE}
              onValueChange={(value) => setBoardId(value === ALL_STATUS_VALUE ? '' : value)}
              defaultLabel={ALL_STATUS_LABEL}
            />
          </div>
        </div>
        <Button
          type="button"
          color="tertiary"
          size="md"
          disabled={!hasSearchConditions}
          onClick={clearSearch}
        >
          <LuX className="size-icon-xs" />
          検索条件をクリア
        </Button>
      </div>

      {/* 候補者リスト（この領域のみスクロール） */}
      <div onScroll={handleScroll} className="hidden-scrollbar min-h-0 flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loading size="small" />
          </div>
        ) : candidates.length === 0 ? (
          <div className="flex h-full items-center justify-center text-body-sm text-sub">
            該当する求職者が見つかりませんでした
          </div>
        ) : (
          // 横スクロール時も全行の幅（＝区切り線）を最長行に揃える
          <div className="flex w-max min-w-full flex-col">
            {candidates.map((candidate) => {
              const isSelected = selectedId === candidate.id;
              const assigneeIds = candidate.organization_users.map((user) => user.id);
              return (
                <div
                  key={candidate.id}
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={0}
                  onClick={() => onSelect(candidate.id, candidate.name)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(candidate.id, candidate.name);
                    }
                  }}
                  className="flex h-15 w-full cursor-pointer items-center gap-md border-b border-solid border-border bg-white pr-lg text-left last:border-b-0 hover:bg-gray-50"
                >
                  {/* 名前・担当者は横スクロールしても左に固定表示する */}
                  <div className="sticky left-0 z-10 flex h-full min-w-68.5 shrink-0 items-center gap-md bg-inherit pl-lg">
                    <span
                      className={clsx(
                        'flex size-4 shrink-0 items-center justify-center rounded-full border-2 border-solid',
                        isSelected ? 'border-primary bg-primary' : 'border-border bg-white'
                      )}
                    >
                      {isSelected && <span className="size-1.5 rounded-full bg-white" />}
                    </span>
                    <span className="text-heading-xs whitespace-nowrap text-main">
                      {candidate.name}
                      {candidate.age !== null && ` (${candidate.age})`}
                    </span>
                    <UsersMenu
                      activeUsers={activeUsersData?.activeUsers ?? []}
                      selectedUserIds={assigneeIds}
                      onSelect={(userIds) => void handleUpdateAssignees(candidate.id, userIds)}
                      currentUserId={iam?.current_user?.id}
                    />
                  </div>
                  <div className="flex min-w-86 shrink-0 items-center gap-lg">
                    {candidate.company_name && (
                      <span className="flex shrink-0 items-center gap-xs text-body-xs whitespace-nowrap text-main">
                        <MdApartment className="size-icon-xs shrink-0 text-main" />
                        {candidate.company_name}
                      </span>
                    )}
                    {candidate.job_position && (
                      <span className="flex shrink-0 items-center gap-xs text-body-xs whitespace-nowrap text-main">
                        <MdWork className="size-icon-xs shrink-0 text-main" />
                        {candidate.job_position}
                      </span>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-xs">
                    {candidate.tags.map((tag) => (
                      <Tag key={tag.id} color={tag.color_hex} size="md">
                        {tag.name}
                      </Tag>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {isFetchingNextPage && (
          <div className="flex justify-center py-md">
            <Loading size="small" />
          </div>
        )}
      </div>
    </div>
  );
}
